import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { createFieldNote, fetchFieldNoteByDate, updateFieldNote } from '../../../api/admin';
import ExploreLinksList from './components/ExploreLinksList';

// Mirror of alex-client's `buildExploreSeed` default (app/utils/exploreSeed.js).
// alex-client owns the real fallback; this copy only drives the preview below, so
// the two must change together (ADM-1844).
const DEFAULT_EXPLORE_PREFIX = 'I\'d like to explore this Field Note';

const initialFormData = {
  publication_date: '',
  title: '',
  body: '',
  explore_prompt: '',
};

// Class names below are the Tailwind 0.7 vocabulary this app actually compiles (see
// /tailwind.js). Tailwind 3 idioms — the numeric gray/amber palette, ring-*, transition-*,
// the disabled: variant — silently generate nothing here; ADM-1823 was one of them.
// `focus:outline-none` needs a replacement indicator, or the field a keyboard user is on
// becomes invisible. The `focus:ring-*` pair that used to sit here never generated anything,
// so this page has had no focus indicator at all; a focus border is the 0.7 equivalent.
const inputClass =
  'w-full border border-gray-dark rounded-lg p-3 focus:outline-none focus:border-link-purple';

const buttonBaseClass = 'font-semibold rounded-lg px-5 py-3';

// 0.7 has no `disabled:` variant, so the disabled look is composed from the same boolean
// that disables the button. Colour utilities are kept to one per state — with a single
// stylesheet, two competing text colours would resolve by source order, not by class order.
// matte-gray on gray-dark is 5.6:1; middle-gray would be 2.5:1, under WCAG AA.
const buttonClass = (disabled) =>
  `${buttonBaseClass} ${
    disabled
      ? 'bg-gray-dark text-matte-gray cursor-not-allowed'
      : 'bg-purple-500 text-white hover:bg-link-purple'
  }`;

// "Load saved note" is secondary to "Save & generate links" sitting below it, so it is
// outlined rather than filled. Same 0.7 constraint as buttonClass: no `disabled:` variant,
// so the disabled look comes from the same boolean. matte-gray (not middle-gray) on white
// is 7.4:1; middle-gray would be 3.5:1.
const secondaryButtonClass = (disabled) =>
  `${buttonBaseClass} border ${
    disabled
      ? 'border-gray-dark text-matte-gray cursor-not-allowed'
      : 'border-purple-500 text-link-purple hover:bg-lightest-purple'
  }`;

// The exact payload `create` stores, derived from the form. handleGenerate sends this and
// hasUnsavedEdits compares against it, so the normalisation has exactly one definition.
// That is the point: the title is trimmed on the way out and the server echoes the trimmed
// string back verbatim (no strip_attributes, no model callbacks), so a comparison that
// forgot to trim would read a trailing space as "edited" forever and hide the links for a
// note that is perfectly in sync. `body` is deliberately NOT trimmed — the server stores it
// verbatim, so trimming it here would be the same bug pointing the other way.
const savedPayload = (data) => ({
  publication_date: data.publication_date,
  title: data.title.trim(),
  body: data.body,
});

// Whether the form has drifted from the note the server last saved (ADM-1824). Derived on
// every render rather than tracked in a `dirty` flag: reverting an edit brings the links
// straight back, and no flag can be left set by a path that forgot to clear it. A flag is
// specifically wrong here — re-saving the same date returns the same id AND the same
// publication_date, so a useEffect keyed on the saved note's fields would never fire and a
// successful save would leave the links hidden.
//
// publication_date is compared as a string on purpose: the serializer emits a Ruby Date and
// Rails renders it "2026-06-22", the same format an <input type="date"> value holds. That
// wire format is pinned by test/controllers/api/v2/admin/field_notes_controller_test.rb.
//
// Invariant: this comparison assumes formData was seeded from the same note as fieldNote.
// The writers of fieldNote are a save of formData itself and the "Load saved note" fetch
// (ADM-1844), which sets both in the same handler for exactly this reason — a fetch that
// set fieldNote alone would leave the three empty initialFormData strings mismatching
// everything, and the page would show "Unsaved edits" on a note nobody had touched.
// Both sides of the title are trimmed. Trimming only the form side was safe while a save
// was the only way a note reached fieldNote, since the save trims on the way out. "Load
// saved note" broke that: it puts a server-authored title into the form verbatim, so a
// stored title carrying surrounding whitespace would mismatch on the first render after a
// load and pin "Unsaved edits" on a note nobody touched — unclearable, because there is no
// edit to revert. WordpressFieldNoteSync writes `title: rendered(post['title'])` with no
// trim, and those backfilled notes are exactly the population Load exists to serve.
const hasUnsavedEdits = (fieldNote, formData) => {
  if (!fieldNote) return false;

  const sent = savedPayload(formData);

  return (
    sent.publication_date !== fieldNote.publication_date ||
    sent.title !== (fieldNote.title || '').trim() ||
    sent.body !== fieldNote.body
  );
};

// Shows exactly what the ALEX composer will prefill: the note's own opening line
// (or the default prefix when blank), followed by the title — linked once the
// WordPress permalink is recorded, plain text until then.
//
// Classes are the Tailwind 0.7 vocabulary documented above; `faint-charcoal` and
// `matte-gray` are the real names for what would be `bg-gray-100`/`text-gray-600`
// in a Tailwind 3 build, where they would generate nothing at all.
const ComposerPreview = ({ explorePrompt, title, wordpressUrl }) => {
  const line = (explorePrompt || '').trim() || DEFAULT_EXPLORE_PREFIX;
  const shownTitle = (title || '').trim() || '<field note title>';

  return (
    <div className="mb-4 p-3 rounded-lg bg-faint-charcoal">
      <span className="block mb-1 text-sm font-medium text-matte-gray">
        ALEX prompt box will show
      </span>
      <span>
        {line}:{' '}
        {wordpressUrl ? (
          <a
            href={wordpressUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-link-purple underline"
          >
            {shownTitle}
          </a>
        ) : (
          shownTitle
        )}
      </span>
    </div>
  );
};

const FieldNotes = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [fieldNote, setFieldNote] = useState(null);
  const [wordpressUrl, setWordpressUrl] = useState('');
  const [error, setError] = useState(null);

  // The composer line has a single home: `formData.explore_prompt` above. Echo
  // the saved value back into it so a re-save can't resurrect a stale edit.
  const adoptSaved = (data) => {
    setError(null);
    setFieldNote(data.field_note);
    setWordpressUrl(data.field_note.wordpress_url || '');
    setFormData((current) => ({
      ...current,
      explore_prompt: data.field_note.explore_prompt || '',
    }));
  };

  const generate = useMutation(createFieldNote, {
    onSuccess: adoptSaved,
    onError: (e) => setError(e?.response?.data?.message || e.message),
  });

  // Step 2 saves the post-publication edits together: the permalink and any
  // revision to the composer line typed into the field above.
  const update = useMutation((attributes) => updateFieldNote(fieldNote.id, attributes), {
    onSuccess: adoptSaved,
    onError: (e) => setError(e?.response?.data?.message || e.message),
  });

  // Pulls a note already stored for the entered date into the form, so a
  // published note can be reworded (or its permalink recorded) in a fresh page
  // session without re-pasting the body.
  const load = useMutation(() => fetchFieldNoteByDate(formData.publication_date), {
    onSuccess: (note) => {
      if (!note) {
        setError(`No field note stored for ${formData.publication_date}.`);
        return;
      }
      setError(null);
      setFieldNote(note);
      setWordpressUrl(note.wordpress_url || '');
      setFormData({
        publication_date: note.publication_date,
        title: note.title || '',
        body: note.body || '',
        explore_prompt: note.explore_prompt || '',
      });
    },
    onError: (e) => setError(e?.response?.data?.message || e.message),
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  // explore_prompt rides alongside savedPayload rather than inside it: the drift
  // comparison (ADM-1824) must stay on the three fields the explore links are
  // derived from. Folding the composer line in would hide the result block the
  // moment the line was edited — and that block is where the line gets saved
  // after publication, so the edit could never be submitted.
  const handleGenerate = (event) => {
    event.preventDefault();
    generate.mutate({
      ...savedPayload(formData),
      explore_prompt: formData.explore_prompt.trim(),
    });
  };

  const isFormValid = () =>
    formData.publication_date && formData.title.trim() && formData.body.trim();

  const generateDisabled = !isFormValid() || generate.isLoading;
  const loadDisabled = !formData.publication_date || load.isLoading;
  const recordDisabled = !wordpressUrl.trim() || update.isLoading;
  const unsavedEdits = hasUnsavedEdits(fieldNote, formData);
  const showResult = Boolean(fieldNote) && !unsavedEdits;

  return (
    <div className="p-8" style={{ maxWidth: '760px' }}>
      <h1 className="text-2xl font-bold mb-2">Field Notes — Explore with ALEX</h1>
      {/* No text colour on <p>/<h*> here: `.admin-user p, .admin-user h1, ... { color: #3c3c3c }`
          is a two-class selector and outranks any single-class text-* utility. `text-gray-700`
          used to sit here and was doubly dead — phantom in this build AND outranked. */}
      <p className="mb-8">
        Paste the proofed field note <strong>before publishing</strong>. We store it and hand
        back the per-channel &ldquo;Explore with ALEX&rdquo; links to embed in the HubSpot,
        Substack, and WordPress posts. After publishing, record the WordPress URL below.
        Already saved a note? Enter its date and choose <strong>Load saved note</strong> to edit
        it.
      </p>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-lightest text-red-darker">{error}</div>
      )}

      {/* Step 1 — Generate */}
      <form onSubmit={handleGenerate} className="mb-8">
        <label className="block mb-1 font-medium" htmlFor="publication_date">
          Publication date
        </label>
        <div className="flex gap-3 mb-4">
          <input
            id="publication_date"
            name="publication_date"
            type="date"
            className={inputClass}
            value={formData.publication_date}
            onChange={handleChange}
          />
          <button
            type="button"
            onClick={() => load.mutate()}
            disabled={loadDisabled}
            className={`${secondaryButtonClass(loadDisabled)} whitespace-no-wrap`}
          >
            {load.isLoading ? 'Loading…' : 'Load saved note'}
          </button>
        </div>

        <label className="block mb-1 font-medium" htmlFor="title">Title</label>
        <input
          id="title"
          name="title"
          type="text"
          className={`${inputClass} mb-4`}
          value={formData.title}
          onChange={handleChange}
        />

        <label className="block mb-1 font-medium" htmlFor="explore_prompt">
          ALEX prompt box line <span className="font-normal text-matte-gray">(optional)</span>
        </label>
        {/* No text colour: `.admin-user p { color: #3c3c3c }` is a two-class selector and
            outranks any single-class text-* utility here (see the note on the intro <p>). */}
        <p className="mb-1 text-sm">
          Match the call-to-action sentence at the end of this note, so readers see they landed
          in the right place. Leave blank for the standard copy. Plain text, 255 characters max —
          the note title is appended automatically.
        </p>
        <input
          id="explore_prompt"
          name="explore_prompt"
          type="text"
          maxLength={255}
          placeholder={DEFAULT_EXPLORE_PREFIX}
          className={`${inputClass} mb-4`}
          value={formData.explore_prompt}
          onChange={handleChange}
        />

        <ComposerPreview
          explorePrompt={formData.explore_prompt}
          title={formData.title}
          wordpressUrl={fieldNote?.wordpress_url}
        />

        <label className="block mb-1 font-medium" htmlFor="body">Body</label>
        <textarea
          id="body"
          name="body"
          rows={10}
          className={`${inputClass} mb-4`}
          value={formData.body}
          onChange={handleChange}
        />

        <button
          type="submit"
          disabled={generateDisabled}
          className={buttonClass(generateDisabled)}
        >
          {generate.isLoading ? 'Saving…' : 'Save & generate links'}
        </button>
      </form>

      {/* Both halves of the result block below describe the note the server has, so both go
          the moment the form stops matching it. A link copied from a stale block points at
          content that is no longer on screen, and Record URL is worse than stale: it closes
          over fieldNote.id, so once the publication date is what changed it would write the
          published permalink onto a different row entirely. Removing the button from the tree
          is enough — update.mutate() has exactly one caller and React Query does not retry
          mutations. A div, not a p: `.admin-user p { color: #3c3c3c }` is a two-class selector
          and would outrank text-orange-darker.

          The composer line is deliberately outside that comparison (see handleGenerate), so
          this block stays visible while only the line is edited — which is what makes the
          button below able to save it. */}
      {unsavedEdits && (
        <div role="status" className="mb-6 p-4 rounded-lg bg-orange-lightest text-orange-darker">
          <strong>Unsaved edits.</strong> Save again to get the explore links for the text now
          in the form.
        </div>
      )}

      {/* Result — explore URLs + step 2 */}
      {showResult && (
        <div className="border-t border-gray-dark pt-6">
          <h2 className="text-lg font-bold mb-3">
            Explore links for {fieldNote.publication_date}
          </h2>
          <ExploreLinksList exploreLinks={fieldNote.explore_links} />

          <label className="block mb-1 font-medium" htmlFor="wordpress_url">
            WordPress URL{' '}
            {fieldNote.wordpress_url_pending ? (
              <span className="text-orange-dark">(pending)</span>
            ) : (
              <span className="text-green-dark">(recorded)</span>
            )}
          </label>
          <div className="flex gap-3">
            <input
              id="wordpress_url"
              type="url"
              className={inputClass}
              placeholder="https://admiredleadership.com/field-notes-archives/…"
              value={wordpressUrl}
              onChange={(e) => setWordpressUrl(e.target.value)}
            />
            <button
              type="button"
              onClick={() =>
                update.mutate({
                  wordpress_url: wordpressUrl.trim(),
                  // Carries any revision typed into the composer line above, so a
                  // published note can be reworded without re-pasting the body.
                  explore_prompt: formData.explore_prompt.trim(),
                })}
              disabled={recordDisabled}
              className={`${buttonClass(recordDisabled)} whitespace-no-wrap`}
            >
              {update.isLoading ? 'Saving…' : 'Record URL & save line'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldNotes;
